import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { API } from "@/config";
import BootSequence from "@/components/timbotic/BootSequence";
import Header from "@/components/timbotic/Header";
import Hero from "@/components/timbotic/Hero";
import WorkbenchBand from "@/components/timbotic/WorkbenchBand";
import TimbotSection from "@/components/timbotic/TimbotSection";
import RoboticsSection from "@/components/timbotic/RoboticsSection";
import ExperimentsSection from "@/components/timbotic/ExperimentsSection";
import PhilosophySection from "@/components/timbotic/PhilosophySection";
import LocalFirstSection from "@/components/timbotic/LocalFirstSection";
import TelemetrySection from "@/components/timbotic/TelemetrySection";
import FundingSection from "@/components/timbotic/FundingSection";
import EventLogSection from "@/components/timbotic/EventLogSection";
import AboutSection from "@/components/timbotic/AboutSection";
import Footer from "@/components/timbotic/Footer";
import PaymentResult from "@/components/timbotic/PaymentResult";
import BlogPost from "@/components/timbotic/blog/BlogPost";
import TimScribePage from "@/components/timscribe/TimScribePage";
import TimScribeRequirements from "@/components/timscribe/TimScribeRequirements";
import { TIMSCRIBE_ENABLED } from "@/components/timscribe/shared";
import { SafetyDialog, SystemFeed, UnsupervisedOverlay } from "@/components/timbotic/EasterEggs";

const KONAMI = [
    "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
    "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight",
    "b", "a",
];

const SECTION_MESSAGES = {
    timbot: "AGENT 001 OBSERVATION LOG OPENED",
    robotics: "PHYSICAL SYSTEM 001 // DOSSIER ACCESSED",
    experiments: "EXPERIMENT INDEX RETRIEVED — 6 ACTIVE THREADS",
    philosophy: "CORPORATE PHILOSOPHY LOADED (RELUCTANTLY)",
    "local-first": "CLOUD DEPENDENCY: DECLINED",
    telemetry: "AUDIT IN PROGRESS — FIGURES UNVERIFIED",
    funding: "CAPITAL REQUEST ACCESSED — WALLET UNLOCATED",
    logs: "INCIDENT HISTORY: LONGER THAN EXPECTED",
    structure: "PERSONNEL RECORD ACCESSED — CLEARANCE: OWNER",
    footer: "END OF TRANSMISSION",
};

let msgId = 0;

export default function App() {
    const path = window.location.pathname;
    if (path.startsWith("/payment/success")) {
        return <PaymentResult kind="success" />;
    }
    if (path.startsWith("/payment/cancel")) {
        return <PaymentResult kind="cancel" />;
    }
    if (path.startsWith("/blog/")) {
        return <BlogPost slug={path.split("/")[2]} />;
    }
    if (TIMSCRIBE_ENABLED && path.startsWith("/timscribe/system-requirements")) {
        return <TimScribeRequirements />;
    }
    if (TIMSCRIBE_ENABLED && (path === "/timscribe" || path === "/timscribe/")) {
        return <TimScribePage />;
    }

    return <LabPage />;
}

function LabPage() {
    const [booted, setBooted] = useState(false);
    const [coffee, setCoffee] = useState("CRITICAL");
    const [coffeeReserves, setCoffeeReserves] = useState(0);
    const [timbotMood, setTimbotMood] = useState("THINKING");
    const [messages, setMessages] = useState([]);
    const [konamiPhase, setKonamiPhase] = useState(null);
    const [safetyStep, setSafetyStep] = useState(null);

    const pokesRef = useRef(0);
    const konamiIdxRef = useRef(0);
    const seenSectionsRef = useRef(new Set());
    const timersRef = useRef([]);

    const pushMessage = useCallback((text) => {
        const id = ++msgId;
        setMessages((m) => [...m.slice(-2), { id, text }]);
        const t = setTimeout(() => setMessages((m) => m.filter((x) => x.id !== id)), 5200);
        timersRef.current.push(t);
    }, []);

    // Fetch live-ish lab status from the lab itself
    useEffect(() => {
        const url = `${API}/api/system/status`;
        fetch(url)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (data && data.timbot) setTimbotMood(data.timbot);
            })
            .catch(() => {});
        const loadFunds = () =>
            fetch(`${API}/api/funds`)
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => data && setCoffeeReserves(data.coffee.reserves))
                .catch(() => {});
        loadFunds();
        const id = setInterval(loadFunds, 30000);
        return () => clearInterval(id);
    }, []);

    // Coffee acquisition protocol
    const coffeeRef = useRef("CRITICAL");
    const handleCoffee = useCallback(() => {
        if (coffeeRef.current !== "CRITICAL") return;
        coffeeRef.current = "ACQUIRING";
        setCoffee("ACQUIRING");
        pushMessage("COFFEE ACQUISITION PROTOCOL INITIATED");
        const t1 = setTimeout(() => {
            coffeeRef.current = "RESTORED";
            setCoffee("RESTORED");
            pushMessage("COFFEE SYSTEM RESTORED");
            const t2 = setTimeout(() => {
                coffeeRef.current = "CRITICAL";
                setCoffee("CRITICAL");
                pushMessage("COFFEE RESERVES DEPLETED AGAIN");
            }, 45000);
            timersRef.current.push(t2);
        }, 2000);
        timersRef.current.push(t1);
    }, [pushMessage]);

    // Do not poke the agent
    const handlePoke = useCallback(() => {
        pokesRef.current += 1;
        if (pokesRef.current >= 5) {
            pokesRef.current = 0;
            setTimbotMood("ANNOYED");
            pushMessage("STOP POKING THE AGENT.");
            const t = setTimeout(() => setTimbotMood("THINKING"), 5000);
            timersRef.current.push(t);
        }
    }, [pushMessage]);

    // Konami code -> UNSUPERVISED MODE -> ABSOLUTELY NOT.
    useEffect(() => {
        const onKey = (e) => {
            const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
            if (key === KONAMI[konamiIdxRef.current]) {
                konamiIdxRef.current += 1;
            } else {
                konamiIdxRef.current = key === KONAMI[0] ? 1 : 0;
            }
            if (konamiIdxRef.current === KONAMI.length) {
                konamiIdxRef.current = 0;
                setKonamiPhase("transfer");
                document.body.classList.add("unsupervised");
                const t1 = setTimeout(() => setKonamiPhase("denied"), 2000);
                const t2 = setTimeout(() => {
                    setKonamiPhase(null);
                    document.body.classList.remove("unsupervised");
                }, 3500);
                timersRef.current.push(t1, t2);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Scroll-triggered system messages
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !seenSectionsRef.current.has(entry.target.id)) {
                        seenSectionsRef.current.add(entry.target.id);
                        const msg = SECTION_MESSAGES[entry.target.id];
                        if (msg) pushMessage(msg);
                    }
                });
            },
            { threshold: 0.25 }
        );
        Object.keys(SECTION_MESSAGES).forEach((id) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [pushMessage, booted]);

    // Safety system: YES -> REALLY SURE? -> Neither are we.
    const handleSafetyYes = useCallback(() => {
        setSafetyStep((s) => {
            if (s === 1) return 2;
            if (s === 2) {
                const t = setTimeout(() => setSafetyStep(null), 2000);
                timersRef.current.push(t);
                return 3;
            }
            return s;
        });
    }, []);

    useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

    return (
        <div className="App">
            {!booted && <BootSequence onDone={() => setBooted(true)} />}

            <div id="timbotic-root">
                <Header />
                <main>
                    <Hero coffee={coffee} coffeeReserves={coffeeReserves} timbotMood={timbotMood} onCoffee={handleCoffee} onPoke={handlePoke} />
                    <WorkbenchBand />
                    <TimbotSection onPoke={handlePoke} />
                    <RoboticsSection />
                    <ExperimentsSection />
                    <PhilosophySection />
                    <LocalFirstSection />
                    <TelemetrySection />
                    <FundingSection />
                    <EventLogSection />
                    <AboutSection />
                </main>
                <Footer onSafetyTrigger={() => setSafetyStep(1)} />
            </div>

            <SystemFeed messages={messages} />
            <UnsupervisedOverlay phase={konamiPhase} />
            <SafetyDialog step={safetyStep} onYes={handleSafetyYes} />
        </div>
    );
}
